import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EMPLOYEE_ROLES,
  FORM_TYPES,
  type EmployeeRole,
  type FormFieldKind,
  type FormReviewMeta,
  type FormType,
  type IntakeTemplateContent,
  type LocalizedText,
  type TemplateField,
  type TemplateFieldOption,
  type TemplateSection,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { fieldLock } from "@/lib/question-lock";
import {
  fetchIntakeTemplate,
  publishIntakeTemplate,
  saveIntakeDraft,
} from "@/lib/intake-template-api";

// CAR-53 — clinical owner edits the patient intake (anamnes) template.
// The screen mirrors what the patient sees (section-by-section, see
// docs/clinical-validation/intake-flow.html) but every user-facing string is
// editable side-by-side in Swedish + English. Evaluator-pinned fields are
// structurally locked — only their copy may change — because the screening
// rules key off them.

// TES-429 — a condition can rest either on an answer in the same form or on
// patient context from an EARLIER form. Both must be readable here: this is the
// surface where care staff see what the form actually does, and a condition
// shown wrongly is worse than no condition at all.
const CONTEXT_LABEL: Record<string, string> = {
  prescription_transfer: "patienten vill flytta ett recept till Ella",
};

function describeCondition(cond: {
  parentKey?: string;
  triggerValues?: string[];
  contextKey?: string;
}): string {
  // TES-1136 — a condition may combine both; then both must hold.
  const parts: string[] = [];
  if (cond.contextKey) {
    parts.push(CONTEXT_LABEL[cond.contextKey] ?? cond.contextKey);
  }
  if (cond.parentKey) {
    parts.push(`${cond.parentKey} = ${(cond.triggerValues ?? []).join(" | ")}`);
  }
  return `visas om ${parts.join(" och ")}`;
}

const KIND_LABEL: Record<FormFieldKind, string> = {
  numeric: "Tal",
  boolean: "Ja/Nej",
  single_select: "Enval",
  multi_select: "Flerval",
  short_text: "Kort text",
  long_text: "Lång text",
  scale_1_5: "Skala 1–5",
  date: "Datum",
  medications_list: "Läkemedelslista",
  // F-07. Like medications_list it sits outside KIND_ORDER: the field type
  // needs an upload configuration (MIME types, max size, count) that the editor
  // cannot edit yet, so it is authored in the template content — but it must
  // have a name here, or an existing file_upload field has no label in the
  // view.
  file_upload: "Filuppladdning",
  // Simon 2026-09-21. Outside KIND_ORDER like file_upload, but for a different
  // reason: a consent is fully authorable here (a label and `required` is all
  // it has), yet offering it in the dropdown would let the editor turn an
  // ordinary question into one the patient cannot answer "nej" to, and turn a
  // consent back into a question that submits as declined. Which fields are
  // consents is a decision made with the template, not in a select box. The
  // label is needed all the same, or an existing consent field shows up
  // nameless in the editor.
  consent: "Samtycke",
};

const KIND_ORDER: FormFieldKind[] = [
  "numeric",
  "boolean",
  "single_select",
  "multi_select",
  "short_text",
  "long_text",
  "scale_1_5",
  "date",
];

const KIND_HAS_OPTIONS = (kind: FormFieldKind) =>
  kind === "single_select" || kind === "multi_select";

// Deep clone so the draft never shares references with the published copy.
// CAR-62: structuredClone preserves `undefined` values (the JSON.parse/
// JSON.stringify round-trip strips them, which silently flipped optional
// fields like `field.help` / `field.numeric.min` between "absent" and
// "present" across save round-trips → triggered guardrail diffs on the
// backend + spurious referential-equality breaks in React renders).
function clone<T>(value: T): T {
  return structuredClone(value);
}

function emptyLocalized(): LocalizedText {
  return { sv: "", en: "" };
}

function move<T>(arr: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= arr.length) return arr;
  const next = arr.slice();
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

// --- small presentational helpers -----------------------------------------

function LocalizedInput({
  label,
  value,
  onChange,
  multiline = false,
  disabled = false,
  placeholder,
}: {
  label: string;
  value: LocalizedText;
  onChange: (next: LocalizedText) => void;
  multiline?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  const base =
    "w-full border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary";
  return (
    <div className="flex flex-col gap-xs">
      <span className="text-xs uppercase tracking-wide text-textSecondary">
        {label}
      </span>
      <div className="grid grid-cols-2 gap-sm">
        {(["sv", "en"] as const).map((locale) => {
          const common = {
            value: value[locale],
            disabled,
            placeholder: placeholder
              ? `${placeholder} (${locale.toUpperCase()})`
              : locale.toUpperCase(),
            onChange: (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => onChange({ ...value, [locale]: e.target.value }),
          };
          return (
            <div key={locale} className="flex flex-col gap-xs">
              <span className="text-[10px] font-medium text-textSecondary">
                {locale === "sv" ? "Svenska" : "Engelska"}
              </span>
              {multiline ? (
                <textarea {...common} className={`${base} min-h-[60px]`} />
              ) : (
                <input {...common} className={base} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// TES-293 — spec §11 review metadata editor. Declarative flags describing how
// submissions are reviewed/routed; enforcement wiring is TES-279/TES-282.
const FORM_TYPE_LABEL: Record<FormType, string> = {
  medical: "Medicinskt",
  coach: "Coach",
  wellness: "Wellness",
  monthly: "Månatligt",
  weekly: "Veckovis",
};

const ROLE_LABEL: Record<EmployeeRole, string> = {
  doctor: "Läkare",
  coach: "Coach",
  admin: "Admin",
};

const META_FLAGS: {
  key: keyof FormReviewMeta;
  label: string;
  hint: string;
}[] = [
  {
    key: "approvalRequired",
    label: "Kräver granskning",
    hint: "Inskickade svar måste granskas innan de får effekt.",
  },
  {
    key: "blocksNextState",
    label: "Blockerar nästa steg",
    hint: "Ogranskade svar stoppar patientens resa framåt.",
  },
  {
    key: "summaryGenerated",
    label: "Sammanfattning genereras",
    hint: "En sammanfattning skapas automatiskt av svaren.",
  },
  {
    key: "riskScoring",
    label: "Riskbedömning",
    hint: "Svaren riskflaggas av utvärderingsreglerna.",
  },
  {
    key: "patientVisibleSummary",
    label: "Patienten ser sammanfattningen",
    hint: "Den (godkända) sammanfattningen visas för patienten.",
  },
  {
    key: "storedInHealthHistory",
    label: "Sparas i vårdhistorik",
    hint: "Svaren ingår i patientens vårdhistorik.",
  },
];

function FormMetaEditor({
  meta,
  onChange,
}: {
  meta: FormReviewMeta;
  onChange: (meta: FormReviewMeta) => void;
}) {
  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <div>
        <h2 className="font-display text-lg">Granskning &amp; routing</h2>
        <p className="text-xs text-textSecondary mt-xs">
          Beskriver hur inskickade svar hanteras. Reglerna börjar verka fullt ut
          när patientresans tillstånd och granskningskön är på plats.
        </p>
      </div>
      <div className="flex flex-wrap gap-md">
        <label className="flex flex-col gap-xs text-sm">
          <span className="text-xs font-semibold text-textSecondary uppercase tracking-wide">
            Formulärtyp
          </span>
          <select
            value={meta.formType}
            onChange={(e) =>
              onChange({ ...meta, formType: e.target.value as FormType })
            }
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            {FORM_TYPES.map((t) => (
              <option key={t} value={t}>
                {FORM_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-xs text-sm">
          <span className="text-xs font-semibold text-textSecondary uppercase tracking-wide">
            Granskas av
          </span>
          <select
            value={meta.requiredRole}
            onChange={(e) =>
              onChange({
                ...meta,
                requiredRole: e.target.value as EmployeeRole,
              })
            }
            className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
          >
            {EMPLOYEE_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-sm">
        {META_FLAGS.map((flag) => (
          <label
            key={flag.key}
            className="flex items-start gap-sm text-sm cursor-pointer"
            title={flag.hint}
          >
            <input
              type="checkbox"
              checked={meta[flag.key] as boolean}
              onChange={(e) =>
                onChange({ ...meta, [flag.key]: e.target.checked })
              }
              className="mt-[3px] accent-brand"
            />
            <span>
              {flag.label}
              <span className="block text-xs text-textSecondary">
                {flag.hint}
              </span>
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

function LockBadge() {
  return (
    <span
      title="Evaluator-låst — används av screeningreglerna. Nyckel, typ och borttagning är låsta."
      className="inline-flex items-center gap-xs px-sm py-xs rounded-pill text-xs font-medium bg-warning/10 text-warning border border-warning/30"
    >
      Låst fält
    </span>
  );
}

// TES-1247 — the field asks a question from the canonical question register.
function QuestionBadge({ question }: { question: string }) {
  return (
    <span
      title="Kopplad till frågeregistret. Fråga, hjälptext, enhet, gränser och svarsalternativ ändras bara i registret, så att samma fråga ställs likadant i alla formulär."
      className="inline-flex items-center gap-xs px-sm py-xs rounded-pill text-xs font-medium bg-surfaceMuted text-textSecondary border border-surfaceMuted"
    >
      Frågeregister: <span className="font-mono">{question}</span>
    </span>
  );
}

function MoveButtons({
  onUp,
  onDown,
  canUp,
  canDown,
}: {
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}) {
  return (
    <div className="flex gap-xs">
      <button
        type="button"
        onClick={onUp}
        disabled={!canUp}
        title="Flytta upp"
        className="px-sm py-xs rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-brand hover:border-brand disabled:opacity-30"
      >
        ↑
      </button>
      <button
        type="button"
        onClick={onDown}
        disabled={!canDown}
        title="Flytta ned"
        className="px-sm py-xs rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-brand hover:border-brand disabled:opacity-30"
      >
        ↓
      </button>
    </div>
  );
}

// --- field editor ----------------------------------------------------------

function FieldEditor({
  field,
  index,
  count,
  onChange,
  onRemove,
  onMove,
}: {
  field: TemplateField;
  index: number;
  count: number;
  onChange: (next: TemplateField) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
}) {
  const pinned = field.evaluatorPinned;
  // TES-1247 — key and kind are locked on pinned and register-bound fields;
  // the copy and scale only on register-bound ones.
  const lock = fieldLock(field);
  const hasOptions = KIND_HAS_OPTIONS(field.kind);

  function patch(partial: Partial<TemplateField>) {
    onChange({ ...field, ...partial });
  }

  function patchOption(optIndex: number, next: TemplateFieldOption) {
    const options = (field.options ?? []).slice();
    options[optIndex] = next;
    patch({ options });
  }

  function addOption() {
    const options = (field.options ?? []).slice();
    options.push({ value: "", label: emptyLocalized() });
    patch({ options });
  }

  function removeOption(optIndex: number) {
    const options = (field.options ?? []).slice();
    options.splice(optIndex, 1);
    patch({ options });
  }

  return (
    <div className="border border-surfaceMuted rounded-md p-md flex flex-col gap-md bg-background">
      <div className="flex items-start justify-between gap-md">
        <div className="flex flex-col gap-xs">
          <div className="flex items-center gap-sm flex-wrap">
            <span className="font-mono text-xs text-textSecondary">
              {field.key || "(ingen nyckel)"}
            </span>
            <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs bg-surfaceMuted text-textSecondary">
              {KIND_LABEL[field.kind]}
            </span>
            {field.required ? (
              <span className="text-xs text-textSecondary">obligatoriskt</span>
            ) : null}
            {field.showWhen ? (
              // Conditions are set by code/imports (patient safety) — shown
              // here, not edited.
              <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs bg-surfaceMuted text-textSecondary">
                {describeCondition(field.showWhen)}
              </span>
            ) : null}
            {pinned ? <LockBadge /> : null}
            {lock.question ? <QuestionBadge question={lock.question} /> : null}
          </div>
        </div>
        <div className="flex items-center gap-sm">
          <MoveButtons
            onUp={() => onMove(-1)}
            onDown={() => onMove(1)}
            canUp={index > 0}
            canDown={index < count - 1}
          />
          <button
            type="button"
            onClick={onRemove}
            disabled={pinned}
            title={pinned ? "Låsta fält kan inte tas bort" : "Ta bort fält"}
            className="px-sm py-xs rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-danger hover:border-danger disabled:opacity-30 disabled:hover:text-textSecondary disabled:hover:border-surfaceMuted"
          >
            Ta bort
          </button>
        </div>
      </div>

      {/* structural props — locked on pinned and register-bound fields */}
      <div className="grid grid-cols-[1fr_max-content_max-content] gap-sm items-end">
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Nyckel
          </span>
          <input
            value={field.key}
            disabled={lock.structure}
            onChange={(e) => patch({ key: e.target.value })}
            className="border border-surfaceMuted rounded-md p-sm text-sm font-mono focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
          />
        </label>
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Typ
          </span>
          <select
            value={field.kind}
            disabled={lock.structure}
            onChange={(e) => {
              const kind = e.target.value as FormFieldKind;
              patch({
                kind,
                options: KIND_HAS_OPTIONS(kind)
                  ? (field.options ?? [])
                  : undefined,
                numeric: kind === "numeric" ? (field.numeric ?? {}) : undefined,
              });
            }}
            className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
          >
            {KIND_ORDER.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-xs pb-sm">
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => patch({ required: e.target.checked })}
          />
          <span className="text-sm">Obligatoriskt</span>
        </label>
      </div>

      {/* copy — editable on pinned fields, locked on register-bound ones */}
      <LocalizedInput
        label="Fråga / etikett"
        value={field.label}
        disabled={lock.copy}
        onChange={(label) => patch({ label })}
      />
      <LocalizedInput
        label="Hjälptext (valfri)"
        value={field.help ?? emptyLocalized()}
        disabled={lock.copy}
        onChange={(help) => patch({ help })}
      />

      <label className="flex flex-col gap-xs max-w-[200px]">
        <span className="text-xs uppercase tracking-wide text-textSecondary">
          Enhet (valfri)
        </span>
        <input
          value={field.unit ?? ""}
          disabled={lock.copy}
          onChange={(e) =>
            patch({ unit: e.target.value ? e.target.value : undefined })
          }
          placeholder="t.ex. kg, mg, år"
          className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
        />
      </label>

      {/* numeric bounds */}
      {field.kind === "numeric" ? (
        <div className="flex flex-wrap gap-md items-end">
          <label className="flex flex-col gap-xs">
            <span className="text-xs uppercase tracking-wide text-textSecondary">
              Min
            </span>
            <input
              type="number"
              value={field.numeric?.min ?? ""}
              disabled={lock.copy}
              onChange={(e) =>
                patch({
                  numeric: {
                    ...field.numeric,
                    min:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  },
                })
              }
              className="border border-surfaceMuted rounded-md p-sm text-sm w-28 focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
            />
          </label>
          <label className="flex flex-col gap-xs">
            <span className="text-xs uppercase tracking-wide text-textSecondary">
              Max
            </span>
            <input
              type="number"
              value={field.numeric?.max ?? ""}
              disabled={lock.copy}
              onChange={(e) =>
                patch({
                  numeric: {
                    ...field.numeric,
                    max:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  },
                })
              }
              className="border border-surfaceMuted rounded-md p-sm text-sm w-28 focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
            />
          </label>
          <label className="flex items-center gap-xs pb-sm">
            <input
              type="checkbox"
              checked={field.numeric?.integer ?? false}
              disabled={lock.copy}
              onChange={(e) =>
                patch({
                  numeric: { ...field.numeric, integer: e.target.checked },
                })
              }
            />
            <span className="text-sm">Endast heltal</span>
          </label>
        </div>
      ) : null}

      {/* options */}
      {hasOptions ? (
        <div className="flex flex-col gap-sm">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Svarsalternativ
          </span>
          {(field.options ?? []).map((opt, optIndex) => (
            // CAR-62: stable key derived from the option's own `value` —
            // `key={optIndex}` would re-mount components against the wrong
            // row when the editor reorders/removes options, dragging
            // focused-input state into the wrong slot.
            <div
              key={`${opt.value}-${optIndex}`}
              className="border border-surfaceMuted rounded-md p-sm flex flex-col gap-sm"
            >
              <div className="flex items-end justify-between gap-sm">
                <label className="flex flex-col gap-xs flex-1">
                  <span className="text-[10px] uppercase tracking-wide text-textSecondary">
                    Värde (lagras)
                  </span>
                  <input
                    value={opt.value}
                    disabled={lock.copy}
                    onChange={(e) =>
                      patchOption(optIndex, { ...opt, value: e.target.value })
                    }
                    className="border border-surfaceMuted rounded-md p-sm text-sm font-mono focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeOption(optIndex)}
                  disabled={lock.copy}
                  className="px-sm py-xs rounded-md border border-surfaceMuted text-xs text-textSecondary hover:text-danger hover:border-danger disabled:opacity-30"
                >
                  Ta bort
                </button>
              </div>
              <LocalizedInput
                label="Visad text"
                value={opt.label}
                disabled={lock.copy}
                onChange={(label) => patchOption(optIndex, { ...opt, label })}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addOption}
            disabled={lock.copy}
            className="self-start px-md py-xs rounded-md border border-dashed border-surfaceMuted text-sm text-textSecondary hover:text-brand hover:border-brand disabled:opacity-30"
          >
            + Lägg till alternativ
          </button>
        </div>
      ) : null}
    </div>
  );
}

// --- section editor --------------------------------------------------------

function SectionEditor({
  section,
  index,
  count,
  onChange,
  onRemove,
  onMove,
}: {
  section: TemplateSection;
  index: number;
  count: number;
  onChange: (next: TemplateSection) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
}) {
  function patch(partial: Partial<TemplateSection>) {
    onChange({ ...section, ...partial });
  }

  function patchField(fieldIndex: number, next: TemplateField) {
    const fields = section.fields.slice();
    fields[fieldIndex] = next;
    patch({ fields });
  }

  function addField() {
    const fields = section.fields.slice();
    fields.push({
      key: "",
      kind: "short_text",
      label: emptyLocalized(),
      required: false,
      evaluatorPinned: false,
    });
    patch({ fields });
  }

  function removeField(fieldIndex: number) {
    const fields = section.fields.slice();
    fields.splice(fieldIndex, 1);
    patch({ fields });
  }

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md">
      <header className="bg-surfaceMuted/50 border-b border-surfaceMuted px-md py-sm flex items-start justify-between gap-md">
        <div className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Sektion {index + 1} av {count}
          </span>
          <span className="font-mono text-xs text-textSecondary">
            {section.id}
          </span>
        </div>
        <div className="flex items-center gap-sm">
          <MoveButtons
            onUp={() => onMove(-1)}
            onDown={() => onMove(1)}
            canUp={index > 0}
            canDown={index < count - 1}
          />
          <button
            type="button"
            onClick={onRemove}
            className="px-sm py-xs rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-danger hover:border-danger"
          >
            Ta bort sektion
          </button>
        </div>
      </header>

      <div className="p-md flex flex-col gap-md">
        <LocalizedInput
          label="Sektionsrubrik"
          value={section.title}
          onChange={(title) => patch({ title })}
        />
        <LocalizedInput
          label="Beskrivning (valfri)"
          value={section.description ?? emptyLocalized()}
          onChange={(description) => patch({ description })}
          multiline
        />

        <div className="flex flex-col gap-sm">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Fält ({section.fields.length})
          </span>
          {section.fields.length === 0 ? (
            <p className="text-sm text-textSecondary">
              Inga fält i den här sektionen ännu.
            </p>
          ) : null}
          {section.fields.map((field, fieldIndex) => (
            // CAR-62: stable key from the field's own `key` (unique within
            // section by design). `key={fieldIndex}` would carry
            // FieldEditor state (focused input, draft text) onto the wrong
            // field after a reorder.
            <FieldEditor
              key={field.key}
              field={field}
              index={fieldIndex}
              count={section.fields.length}
              onChange={(next) => patchField(fieldIndex, next)}
              onRemove={() => removeField(fieldIndex)}
              onMove={(delta) =>
                patch({ fields: move(section.fields, fieldIndex, delta) })
              }
            />
          ))}
          <button
            type="button"
            onClick={addField}
            className="self-start px-md py-sm rounded-md border border-dashed border-surfaceMuted text-sm text-textSecondary hover:text-brand hover:border-brand"
          >
            + Lägg till fält
          </button>
        </div>
      </div>
    </section>
  );
}

// --- published reference (read-only) --------------------------------------

// Collapsed-by-default snapshot of the live published template so the clinician
// can compare the draft against what patients currently see.
function PublishedReference({ content }: { content: IntakeTemplateContent }) {
  return (
    <details className="bg-surface border border-surfaceMuted rounded-md">
      <summary className="px-md py-sm cursor-pointer text-sm font-medium select-none">
        Publicerad version v{content.version} (skrivskyddad referens)
      </summary>
      <div className="px-md pb-md pt-xs flex flex-col gap-md border-t border-surfaceMuted">
        <div>
          <p className="font-medium text-sm">{content.title.sv}</p>
          <p className="text-xs text-textSecondary italic">
            {content.title.en}
          </p>
        </div>
        {content.sections.map((section) => (
          <div key={section.id} className="flex flex-col gap-xs">
            <p className="text-sm font-medium">
              {section.title.sv}
              <span className="text-textSecondary font-normal italic">
                {" "}
                — {section.title.en}
              </span>
            </p>
            <ul className="flex flex-col gap-xs pl-md">
              {section.fields.map((field) => (
                <li key={field.key} className="text-xs text-textSecondary">
                  <span className="font-mono">{field.key}</span>{" "}
                  <span className="text-textPrimary">{field.label.sv}</span>
                  <span className="italic"> / {field.label.en}</span>
                  <span> · {KIND_LABEL[field.kind]}</span>
                  {field.evaluatorPinned ? <span> · låst</span> : null}
                  {field.questionId ? (
                    <span> · frågeregister {field.questionId}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </details>
  );
}

// --- route -----------------------------------------------------------------

// CAR-92: the editor is generic over an editable template kind. `templateKey`
// selects which one (intake by default); `heading`/`intro` label it. The same
// component drives both the intake ("Hälsodeklaration") and weekly check-in
// editors — the field/section editing UX is identical.
export function IntakeEditorRoute({
  templateKey = "intake",
  heading = "Hälsodeklaration",
  intro = "Redigera anamnesformuläret patienten fyller i. Ändringar sparas som ett utkast och börjar gälla först när du publicerar.",
}: {
  templateKey?: string;
  heading?: string;
  intro?: string;
} = {}) {
  const { signOut } = useAuth();
  const [published, setPublished] = useState<IntakeTemplateContent | null>(
    null,
  );
  const [hadDraft, setHadDraft] = useState(false);
  const [draft, setDraft] = useState<IntakeTemplateContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"save" | "publish" | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  // CAR-62: dirty bit so the beforeunload guard can warn before the user
  // closes the tab with unsaved clinical content. Set true on every
  // patch* call; cleared on save / publish / fresh load. Tracked as a
  // ref because the beforeunload handler doesn't need re-renders to read
  // the current value.
  const dirtyRef = useRef(false);

  const load = useCallback(() => {
    fetchIntakeTemplate(templateKey)
      .then(({ published: pub, draft: drf }) => {
        setPublished(pub);
        setHadDraft(drf !== null);
        // Editable draft: the saved draft if one exists, else a fresh copy of
        // the published version (with the version left as-is — the backend
        // assigns the next number on publish).
        setDraft(clone(drf ?? pub));
        dirtyRef.current = false;
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [signOut, templateKey]);

  useEffect(load, [load]);

  // CAR-62: warn before unload (tab close / hard navigation) when the
  // editor has unsaved changes. The browser surfaces a generic prompt;
  // setting returnValue on the event triggers it in modern Chromium/Safari.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      // Required for legacy browsers; modern ones ignore the string and
      // render their own copy. Both gate on `preventDefault` + a truthy
      // returnValue.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const sectionCount = draft?.sections.length ?? 0;
  const fieldCount = useMemo(
    () => draft?.sections.reduce((sum, s) => sum + s.fields.length, 0) ?? 0,
    [draft],
  );

  function patchDraft(partial: Partial<IntakeTemplateContent>) {
    dirtyRef.current = true;
    setDraft((d) => (d ? { ...d, ...partial } : d));
  }

  function patchSection(index: number, next: TemplateSection) {
    dirtyRef.current = true;
    setDraft((d) => {
      if (!d) return d;
      const sections = d.sections.slice();
      sections[index] = next;
      return { ...d, sections };
    });
  }

  function addSection() {
    dirtyRef.current = true;
    setDraft((d) => {
      if (!d) return d;
      return {
        ...d,
        sections: [
          ...d.sections,
          {
            id: "",
            title: emptyLocalized(),
            fields: [],
          },
        ],
      };
    });
  }

  function removeSection(index: number) {
    dirtyRef.current = true;
    setDraft((d) => {
      if (!d) return d;
      const sections = d.sections.slice();
      sections.splice(index, 1);
      return { ...d, sections };
    });
  }

  function moveSection(index: number, delta: number) {
    dirtyRef.current = true;
    setDraft((d) =>
      d ? { ...d, sections: move(d.sections, index, delta) } : d,
    );
  }

  async function onSave() {
    if (!draft) return;
    setBusy("save");
    setError(null);
    setNotice(null);
    try {
      const saved = await saveIntakeDraft(draft, templateKey);
      setDraft(clone(saved));
      setHadDraft(true);
      dirtyRef.current = false;
      setNotice("Utkast sparat.");
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      // IntakeTemplateError carries the backend guardrail message verbatim
      // (e.g. a removed/re-keyed/re-typed pinned field).
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onPublish() {
    setBusy("publish");
    setError(null);
    setNotice(null);
    try {
      const pub = await publishIntakeTemplate(templateKey);
      setPublished(pub);
      setHadDraft(false);
      setDraft(clone(pub));
      dirtyRef.current = false;
      setConfirmPublish(false);
      setNotice(`Publicerad — version ${pub.version} är nu aktiv.`);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
      setConfirmPublish(false);
    } finally {
      setBusy(null);
    }
  }

  if (!draft || !published) {
    return (
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl tracking-tight">{heading}</h1>
        {error ? (
          <span className="text-danger text-sm">{error}</span>
        ) : (
          <span className="text-textSecondary text-sm">Hämtar…</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      <header className="flex items-start justify-between gap-md">
        <div>
          <h1 className="font-display text-3xl tracking-tight">{heading}</h1>
          <p className="text-textSecondary text-sm mt-xs">{intro}</p>
          <p className="text-xs text-textSecondary mt-xs">
            Publicerad version:{" "}
            <span className="font-medium text-textPrimary">
              v{published.version}
            </span>{" "}
            •{" "}
            {hadDraft ? (
              <span className="text-warning font-medium">
                Opublicerat utkast finns
              </span>
            ) : (
              <span>Inget opublicerat utkast</span>
            )}{" "}
            • {sectionCount} sektioner • {fieldCount} fält
          </p>
        </div>
        <div className="flex gap-sm shrink-0">
          <button
            type="button"
            onClick={onSave}
            disabled={busy !== null}
            className="px-md py-sm rounded-md border border-surfaceMuted text-sm hover:border-brand hover:text-brand disabled:opacity-60"
          >
            {busy === "save" ? "Sparar…" : "Spara utkast"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmPublish(true)}
            disabled={busy !== null}
            className="px-md py-sm rounded-md bg-brand text-textOnBrand text-sm hover:opacity-90 disabled:opacity-60"
          >
            Publicera
          </button>
        </div>
      </header>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm whitespace-pre-wrap">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="bg-success/10 border border-success/30 text-success rounded-md p-md text-sm">
          {notice}
        </div>
      ) : null}

      {/* Read-only snapshot of what patients currently see */}
      <PublishedReference content={published} />

      {/* Template-level copy */}
      <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
        <h2 className="font-display text-lg">Formulärets rubrik (utkast)</h2>
        <LocalizedInput
          label="Titel"
          value={draft.title}
          onChange={(title) => patchDraft({ title })}
        />
        <LocalizedInput
          label="Beskrivning"
          value={draft.description}
          onChange={(description) => patchDraft({ description })}
          multiline
        />
      </section>

      {/* TES-293 — spec §11 review metadata. Declarative for now: describes
          how submissions are reviewed/routed; enforcement (journey gating,
          approval routing) lands with TES-279/TES-282. */}
      {draft.meta ? (
        <FormMetaEditor
          meta={draft.meta}
          onChange={(meta) => patchDraft({ meta })}
        />
      ) : null}

      {/* Sections */}
      {draft.sections.map((section, index) => (
        // CAR-62: stable key from the section's `id` (UUID, set at template
        // creation). `key={index}` would carry SectionEditor + nested
        // FieldEditor state onto the wrong section after a reorder/remove.
        <SectionEditor
          key={section.id ?? `idx-${index}`}
          section={section}
          index={index}
          count={draft.sections.length}
          onChange={(next) => patchSection(index, next)}
          onRemove={() => removeSection(index)}
          onMove={(delta) => moveSection(index, delta)}
        />
      ))}

      <button
        type="button"
        onClick={addSection}
        className="self-start px-md py-sm rounded-md border border-dashed border-surfaceMuted text-sm text-textSecondary hover:text-brand hover:border-brand"
      >
        + Lägg till sektion
      </button>

      {/* Publish confirm */}
      {confirmPublish ? (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-md z-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-intake-title"
            className="bg-surface rounded-md p-lg max-w-md w-full flex flex-col gap-md"
          >
            <h3 id="publish-intake-title" className="font-display text-xl">
              Publicera hälsodeklaration
            </h3>
            <p className="text-textSecondary text-sm">
              Det aktuella utkastet promotas till en ny, oföränderlig publicerad
              version. Nya patienter får direkt det uppdaterade formuläret.
              Spara utkastet först om du har osparade ändringar.
            </p>
            <div className="flex gap-sm justify-end">
              <button
                type="button"
                onClick={() => setConfirmPublish(false)}
                disabled={busy !== null}
                className="px-md py-sm rounded-md text-textSecondary hover:bg-surfaceMuted text-sm"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={onPublish}
                disabled={busy !== null}
                className="px-md py-sm rounded-md bg-brand text-textOnBrand text-sm hover:opacity-90 disabled:opacity-60"
              >
                {busy === "publish" ? "Publicerar…" : "Publicera"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
